/* eslint-disable no-template-curly-in-string */
/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable max-len */
import { css } from '@emotion/css';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { ReactComponent as InfoIcon } from '^/assets/icons/ic_info_black.svg';
import { CarInfoCardMobile } from '^/components/CarInfoCard';
import { VENDOR_LOGOS } from '^/constants/channel/logoImages.constants';
import CancellationAndRefundHertzPOD from '^/pages/Booking/components/DriverDetails/components/CancellationAndRefund/CancellationAndRefund.Hertz.POD';
import CancellationAndRefundHertzPPD from '^/pages/Booking/components/DriverDetails/components/CancellationAndRefund/CancellationAndRefund.Hertz.PPD';
import { getCarName } from '^/util/getCarName';
import { getTermsAndConditionCancellationRichContent } from '^/pages/Booking/renewal/components/TermsAndConditions/TermsAndConditionCancellation.Rich';
import { getTermsAndConditionCancellationTumonContent } from '^/pages/Booking/renewal/components/TermsAndConditions/TermsAndConditionCancellation.Tumon';
import { translateSimplifiedServices } from '^/pages/Listing/common/translateServices';
import {
  isValidExtraEquipments,
  ReservationDetailed,
} from '^/types/ReservationDetailed/RawReservationDetailed';
import { CancellationWarningBeforePickup72Hours } from '^/pages/ReservationDetail/CancellationWarningBeforePickup72hours';
import { parseRentDateTime } from '^/util/rentDateTime';
import { getTermsAndConditionCancellationOrixContent } from '^/pages/Booking/renewal/components/TermsAndConditions/TermsAndConditionCancellation.Orix';
import { ReservationDetailMobileOfficeInfoCard } from '../ReservationDetailMobile.OfficeInfoCard';

export const ReservationDetailsVehicleInfo: React.FC<{
  data: ReservationDetailed;
  onClickCancelReservation(reservationToCancel: ReservationDetailed, e: React.MouseEvent): void;
  onClickShowInsuranceInfo(): void;
}> = observer(function ReservationDetailsVehicleInfo({
  data,
  onClickCancelReservation,
  onClickShowInsuranceInfo,
}) {
  const { t } = useTranslation();

  const { parsedPickupTime, parsedReturnTime, timeDiff } = parseRentDateTime(
    data.pickup_datetime,
    data.return_datetime,
    'YYYY.MM.DD (ddd) HH:mm',
  );

  const insurance = data.insurance_info_json;
  const validEquips = isValidExtraEquipments(data.extra_equipments_json)
    ? data.extra_equipments_json
    : null;
  const services =
    data.services_json?.length > 0
      ? data.services_json
      : (() => {
          try {
            return data.services ? JSON.parse(data.services) : [];
          } catch (error) {
            return [];
          }
        })();

  const getCancellationTerms = () => {
    if (data.supplier === 'RC') {
      return getTermsAndConditionCancellationRichContent();
    }
    if (data.supplier === 'TM') {
      return getTermsAndConditionCancellationTumonContent();
    }
    if (data.pay_type === 'PPD') {
      if (data.supplier === 'OR') {
        return getTermsAndConditionCancellationOrixContent({
          freeCancelDeadline: data.free_cancel_deadline,
          cancelPolicy: data.cancel_policy,
        });
      }
      return <CancellationAndRefundHertzPPD cancelPolicy={data.cancel_policy} />;
    }
    return <CancellationAndRefundHertzPOD />;
  };

  return (
    <div className="flex flex-col gap-[30px]">
      <CarInfoCardMobile
        base={{
          vendor: data.supplier,
          vendorLogoUrl: VENDOR_LOGOS[data.supplier],
          carImageUrl: data.image || '/NOT_FOUND_CAR.png',
          carSizeLabel: `[${t(data.size_car)}]`,
          carModelName: getCarName(data.car_model) || data.actualModelName || data.car_model,
          carSpecText: translateSimplifiedServices(services, t),
          footerNote: '실제 대여하실 차량과 다를 수 있습니다.',
        }}
      />

      <section className="flex flex-col gap-[15px]">
        <div className="flex flex-col gap-3">
          <p className="text-big font-bold">예약 번호</p>
          <p className="text-default font-light">{data.is_confirm_hertz ? data.res_id : '-'}</p>
        </div>
        <div className="flex flex-col gap-3">
          <p className="text-big font-bold">대여/반납 일시</p>
          <p className="text-default font-light">
            {`${parsedPickupTime} ~ 
            ${parsedReturnTime} (${timeDiff})`}
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <p className="text-big font-bold">보험 내용</p>
          <div
            className={[
              css`
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 6px;
              `,
            ].join(' ')}
          >
            <p className="text-default font-light">
              {insurance.code ? t(`insurance_name.${insurance.code}`) : '기본 할인요금'}
            </p>
            <InfoIcon width={20} height={20} onClick={onClickShowInsuranceInfo} />
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <p className="text-big font-bold">추가 옵션</p>
          <p className="text-default font-light">
            {(validEquips?.info?.length || 0) > 0 ? (
              <span>
                {validEquips?.info
                  ?.map((item) => `${t(`equipment_${item.code}`)} (${item.quantity})`)
                  .join(' / ')}
              </span>
            ) : (
              <p>{t('none')}</p>
            )}
          </p>
        </div>
        {/* 영업소 정보 */}
        <ReservationDetailMobileOfficeInfoCard
          title="대여 영업소"
          officeName={data.office_name}
          officeAddress={data.office_address}
          officePhone={data.office_phone}
          officeHours={data.office_time}
        />
        <ReservationDetailMobileOfficeInfoCard
          title="반납 영업소"
          officeName={data.office_second_name}
          officeAddress={data.office_second_address}
          officePhone={data.office_second_phone}
          officeHours={data.office_second_time}
        />
        <div className={'flex flex-col gap-3'}>
          <p className="text-big font-bold">픽업 및 방문 안내</p>
          <p className="text-default font-light">{data.pickup_loc_description}</p>
        </div>
      </section>
      <section>
        <p className="text-semilarge font-bold">취소 및 환불규정</p>
        <div className="mt-4 rounded-[10px] bg-secondary-gray-light p-4 text-default font-light leading-5 mobile:text-small">
          {getCancellationTerms()}
        </div>
      </section>

      {data.res_status === 'booked' && (
        <section>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault(); // 스크롤 상단 이동 방지
              onClickCancelReservation(data, e);
            }}
            disabled={data.cancelDisabled}
            className="
              mt-4 w-full rounded-trimo-5
              bg-trimo-bg p-3
              text-center font-bold
              text-primary-gray hover:bg-primary-skyblue hover:text-white focus:text-white disabled:cursor-not-allowed disabled:bg-secondary-gray disabled:text-neutral-300
            "
          >
            {t('cancelBooking')}
          </button>
          <CancellationWarningBeforePickup72Hours show={data.show72HoursCancelWarning} />
        </section>
      )}
      {data?.supplier === 'ZE' && (
        <div className="my-[24px] text-right">
          <p className="text-default font-bold text-trimo-dark underline underline-offset-1 hover:cursor-pointer">
            <a
              href="https://images.hertz.com/pdfs/asia/KR_HertzGuide2019.pdf"
              target="_blank"
              rel="noreferrer"
            >
              Hertz 해외렌터카 가이드
            </a>
          </p>
        </div>
      )}
    </div>
  );
});
